import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  activateUser as activateUserSvc,
  createUser as createUserSvc,
  deactivateUser as deactivateUserSvc,
  resendInvite as resendInviteSvc,
  updateUser as updateUserSvc,
} from '@/api/services/adminUsersService';
import type {
  AdminUserCreate,
  AdminUserDto,
  AdminUserUpdate,
} from '@/api/types/AdminUser';

import { invalidateDispatchers } from './dispatchersSlice';
import { invalidateUsers } from './usersSlice';

// Thin thunk wrappers around `adminUsersService` mutations. Each one
// dispatches `invalidateDispatchers()` AND `invalidateUsers()` on success so
// the resolver caches that derive from the KC user set
// (dispatchersSlice + usersSlice — feeding `selectAssignedUserName`) are
// re-fetched on next access. Resolves OQ-024 / OQ-026 — until this slice
// existed the `invalidateDispatchers` action had no caller. The two
// invalidations fire together because both slices project from the same
// KC realm: a name/role mutation on one KC user changes BOTH `/api/dispatchers`
// (if the user has the dispatcher role) AND `/api/users-display` (always).

export const createUserAndInvalidate = createAsyncThunk<
  AdminUserDto,
  AdminUserCreate
>('adminUsers/create', async (payload, { dispatch }) => {
  const saved = await createUserSvc(payload);
  dispatch(invalidateDispatchers());
  dispatch(invalidateUsers());
  return saved;
});

export const updateUserAndInvalidate = createAsyncThunk<
  AdminUserDto,
  { sub: string; payload: AdminUserUpdate }
>('adminUsers/update', async ({ sub, payload }, { dispatch }) => {
  const saved = await updateUserSvc(sub, payload);
  dispatch(invalidateDispatchers());
  dispatch(invalidateUsers());
  return saved;
});

export const deactivateUserAndInvalidate = createAsyncThunk<
  AdminUserDto,
  string
>('adminUsers/deactivate', async (sub, { dispatch }) => {
  const updated = await deactivateUserSvc(sub);
  dispatch(invalidateDispatchers());
  dispatch(invalidateUsers());
  return updated;
});

export const activateUserAndInvalidate = createAsyncThunk<
  AdminUserDto,
  string
>('adminUsers/activate', async (sub, { dispatch }) => {
  const updated = await activateUserSvc(sub);
  dispatch(invalidateDispatchers());
  dispatch(invalidateUsers());
  return updated;
});

export const resendInviteAndInvalidate = createAsyncThunk<void, string>(
  'adminUsers/resendInvite',
  async (sub, { dispatch }) => {
    await resendInviteSvc(sub);
    // Resend-invite does NOT mutate name/role/enabled — the resolver caches
    // do not strictly need invalidation. We invalidate anyway for symmetry
    // with the other 3 mutations: KC may flip `emailVerified` (which feeds
    // the `state` projection on `/api/users-display`), so the next fetch
    // observes the fresh state without an explicit special-case.
    dispatch(invalidateDispatchers());
    dispatch(invalidateUsers());
  },
);
